import React, {useEffect} from 'react';
import styled from 'styled-components';
import {useSelector, useDispatch} from 'react-redux';
import {setUserInfo, updateNav} from '../../../redux/actions';
import {useLocation} from 'react-router-dom';
import TreeView from '@material-ui/lab/TreeView';
import NavTreeItem from './NavTreeItem';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {useOktaAuth} from '@okta/okta-react';
import {useUserInfoState} from 'hooks/useUserInfo/UserInfoProvider';

interface Node {
  id: string;
  name: string;
  url: string;
  icon?: string;
  children?: Node[];
  activePaths?: string[];
}

//#region styled components
const StyledNavTree = styled(TreeView)`
  width: 90%;
  display: none;
  @media (min-width: 600px) {
    display: block;
  }
`;

const StyledExpandMoreIcon = styled(ExpandMoreIcon)`
  color: ${props => props.theme.navText};
  fill: ${props => props.theme.navText};
`;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
  color: ${props => props.theme.navText};
  fill: ${props => props.theme.navText};
`;
//#endregion styled components

const navNodes: Node[] = [
  {
    id: 'rrp',
    name: 'RRP Dashboard',
    url: '/rrp',
    icon: 'dashboard',
  },
  {
    id: 'switch',
    name: 'Switch Connectivity',
    url: '/site-search/switch',
    icon: 'switch',
    activePaths: ['/switch'],
  },
  {
    id: 'inventories',
    name: 'Device Inventory',
    url: '/device-inventory/custom',
    icon: 'inventory',
    activePaths: [
      '/device-inventory/na',
      '/device-inventory/emea',
      '/device-inventory/gc',
      '/device-inventory/apla',
    ],
  },
  {
    id: 'storeHealth',
    name: 'Retail Hardware Status',
    url: '/retail-hardware-status',
    icon: 'storeHealth',
  },
  {
    id: 'stores',
    name: 'Stores',
    url: '/stores',
    icon: 'map',
  },
  {
    id: 'registers',
    name: 'Nike POS',
    url: '/npos',
    icon: 'registers',
  },
  {
    id: 'storeServiceManager',
    name: 'Store Service Manager',
    url: '/store-service-manager',
    icon: 'code',
  },
  {
    id: 'contact',
    name: 'Contact',
    url: '/contact',
    icon: 'contact',
  },
  {
    id: 'settings',
    name: 'Settings',
    url: '/settings',
    icon: 'settings',
  },
];

const devNavNodes: Node[] = [
  {
    id: 'styleGuide',
    name: 'Style Guide',
    url: '/styleguide',
    icon: 'code',
  },
  {
    id: 'development',
    name: 'In Development',
    url: '/development',
    icon: 'code',
    children: [
      {
        id: 'newStoreTeam',
        name: 'New Store Team',
        url: '/nst/19',
      },
    ],
  },
];

function renderTree(node: Node) {
  return (
    <NavTreeItem
      key={node.id}
      id={node.id}
      nodeId={node.id}
      label={node.name}
      url={node.url}
      icon={node.icon}
      activePaths={node.activePaths}
    >
      {Array.isArray(node.children) ? node.children.map((node: Node) => renderTree(node)) : null}
    </NavTreeItem>
  );
}

function NavTree() {
  const location = useLocation();
  const dispatch = useDispatch();
  const {oktaAuth} = useOktaAuth();
  const expandedBranches = useSelector((state: any) => state.navigation);

  const isAuthorizedForStoreServiceManager = useSelector(
    (state: any) => state.userInfo.user?.isAuthorizedForStoreServiceManager
  );

  const handleToggle = function handleToggle(nodeIds: string[]) {
    dispatch(updateNav(nodeIds));
  };

  const {
    state: {debugMode},
  } = useUserInfoState();

  useEffect(() => {
    const dispatchSetUserInfo = async () => {
      const user = await oktaAuth.getUser();
      dispatch(setUserInfo(user));
    };

    dispatchSetUserInfo();
  }, [oktaAuth, dispatch]);

  useEffect(() => {
    const routes = [...navNodes, ...devNavNodes];
    const pathname = location.pathname;
    let title = 'Insights';

    for (const item of routes) {
      if (item.children?.find(el => el.url === pathname)) {
        title = 'Insights | ' + item.children.find(el => el.url === pathname)!.name;
        break;
      } else if (item.activePaths?.includes(pathname)) {
        title = 'Insights | ' + item.name;
        break;
      } else if (item.url === pathname) {
        title = 'Insights | ' + item.name;
        break;
      }
    }

    document.title = title;
  }, [location]);

  return (
    <StyledNavTree
      expanded={expandedBranches}
      defaultCollapseIcon={<StyledExpandMoreIcon />}
      defaultExpandIcon={<StyledChevronRightIcon />}
      onNodeToggle={(e, nodeIds: string[]) => {
        handleToggle(nodeIds);
      }}
    >
      {navNodes.map(node => {
        return node.id !== 'storeServiceManager' || isAuthorizedForStoreServiceManager
          ? renderTree(node)
          : null;
      })}

      {/* debug view is meant for users who have admin access */}
      {debugMode ? devNavNodes.map(node => renderTree(node)) : ''}
    </StyledNavTree>
  );
}

export default NavTree;
